import _ from 'lodash'
import moment from 'moment'
import queryString from 'query-string'

import { handleException } from 'clients/exception-handling'
import {
  AppliedFilterOption,
  AppliedFilterOptionRange,
  AppliedFilterOptionSelect,
  ApplyFilterPayload,
  ApplyGroupFilterPayload,
  FilterOption,
  FilterOptionRange,
  FilterOptionSelect,
} from 'features/filters/types'

export enum DateFilterFieldNamesEnum {
  date,
  create_time,
}

export enum HardcodedFilterOptionsEnum {
  CREATE_TIME = 'create_time',
  THEME_GROUP = 'theme_group',
}

export function isAppliedFilterOptionSelect(
  option: AppliedFilterOption,
): option is AppliedFilterOptionSelect {
  return 'values' in option
}

export function isAppliedFilterOptionRange(
  option: AppliedFilterOption,
): option is AppliedFilterOptionSelect {
  return 'min' in option && 'max' in option
}

export const applyFilterValue = (
  payload: ApplyFilterPayload,
  appliedFilters: Array<AppliedFilterOption>,
): Array<AppliedFilterOption> => {
  const { field, value } = payload

  // value_type=0 with no min/max is a "no filter" signal — e.g., the user
  // clicked back to "Reviews & Ratings" on the rating filter without ever
  // engaging the slider. Remove the filter entirely instead of adding an
  // empty range entry, which would otherwise hit the backend's
  // InvalidFilterFieldError path for score with no constraint signal.
  // Note: the payload type technically requires min/max as `number`, but
  // SelectorBody's radio handler passes `undefined` (cast) when the slider
  // hasn't been engaged. Cast locally so the undefined check reflects the
  // actual runtime shape.
  const valueAsRange = value as {
    min?: number
    max?: number
    value_type?: number
  }
  if (
    typeof value === 'object' &&
    valueAsRange.value_type === 0 &&
    valueAsRange.min === undefined &&
    valueAsRange.max === undefined
  ) {
    return appliedFilters.filter((f) => f.field !== field)
  }

  const alreadyAppliedValuesForThisField = appliedFilters.find(
    (f) => f.field === field,
  )
  if (!alreadyAppliedValuesForThisField) {
    return [...appliedFilters, convertFilterPayload(payload)]
  }
  const filteredAppliedFilters = appliedFilters.filter((f) => f.field !== field)
  if (typeof value === 'object') {
    return [...filteredAppliedFilters, convertFilterPayload(payload)]
  }
  if (isAppliedFilterOptionSelect(alreadyAppliedValuesForThisField)) {
    const newValue = value as string
    const values = alreadyAppliedValuesForThisField.values
    const newFilterOption: AppliedFilterOptionSelect = {
      field,
      values: values.includes(newValue)
        ? values.filter((v) => v !== newValue)
        : [...values, newValue],
    }
    if (newFilterOption.values.length) {
      return [...filteredAppliedFilters, newFilterOption]
    }
    return filteredAppliedFilters
  }
  if (isAppliedFilterOptionRange(alreadyAppliedValuesForThisField)) {
    return [...filteredAppliedFilters, convertFilterPayload(payload)]
  }
  return appliedFilters
}

export const applyGroupFilterValue = (
  payload: ApplyGroupFilterPayload,
  appliedFilters: Array<AppliedFilterOption>,
): Array<AppliedFilterOption> => {
  const { field, value } = payload
  const alreadyAppliedValuesForThisField = appliedFilters.find(
    (f) => f.field === field,
  )
  if (!alreadyAppliedValuesForThisField) {
    return [...appliedFilters, convertGroupFilterPayload(payload)]
  }
  const filteredAppliedFilters = appliedFilters.filter((f) => f.field !== field)
  if (isAppliedFilterOptionSelect(alreadyAppliedValuesForThisField)) {
    const newValues = value as string[]
    const values = alreadyAppliedValuesForThisField.values
    const newFilterOption: AppliedFilterOptionSelect = {
      field,
      values: newValues.every((v) => values.includes(v))
        ? values.filter((v) => !newValues.includes(v))
        : [...values, ...newValues],
    }
    if (newFilterOption.values.length) {
      return [...filteredAppliedFilters, newFilterOption]
    }
    return filteredAppliedFilters
  }
  return appliedFilters
}

export const applyFreeInputFilterValue = (
  payload: ApplyGroupFilterPayload,
  appliedFilters: Array<AppliedFilterOption>,
): Array<AppliedFilterOption> => {
  const { field, value } = payload
  const filteredAppliedFilters = appliedFilters.filter((f) => f.field !== field)
  const newValues = value as string[]
  const newFilterOption: AppliedFilterOptionSelect = {
    field,
    values: newValues,
  }
  return [...filteredAppliedFilters, newFilterOption]
}

function convertFilterPayload({
  field,
  value,
}: ApplyFilterPayload): AppliedFilterOptionSelect | AppliedFilterOptionRange {
  if (typeof value === 'object') {
    return { field, ...value }
  }
  return { field, values: [value] }
}

function convertGroupFilterPayload({
  field,
  value,
}: ApplyGroupFilterPayload):
  | AppliedFilterOptionSelect
  | AppliedFilterOptionRange {
  return { field, values: value }
}

export const selectedFiltersCountByComparativeIndex = (
  filterList: AppliedFilterOption[],
): number =>
  filterList.reduce((acc, el) => {
    if (Object.keys(DateFilterFieldNamesEnum).includes(el.field)) {
      return acc
    }
    return acc + (isAppliedFilterOptionSelect(el) ? el.values.length : 1)
  }, 0)

export function reducerToComputeFiltersCount(
  sum: number,
  item: AppliedFilterOption,
): number {
  if (Object.keys(DateFilterFieldNamesEnum).includes(item.field)) {
    return sum
  }

  const numberOfFilters = isAppliedFilterOptionSelect(item)
    ? item.field.includes('sentiment')
      ? item.values.filter((item) => item !== '1' && item !== '0').length
      : item.values.length
    : 1

  return sum + numberOfFilters
}

export function convertFiltersToUriParams(
  values: Array<AppliedFilterOption>,
): Record<string, string | string[]> {
  return values.reduce((res, current) => {
    if (isAppliedFilterOptionSelect(current)) {
      return { ...res, [current.field]: current.values }
    }
    if (isAppliedFilterOptionRange(current)) {
      if (current.field.match(/score_\d/))
        return {
          ...res,
          [current.field]: JSON.stringify({
            min: current.min,
            max: current.max,
            value_type: current.value_type,
          }),
        }
      return {
        ...res,
        [current.field]: JSON.stringify({ min: current.min, max: current.max }),
      }
    }
    return res
  }, {})
}

export function formatUriParams(values: Array<AppliedFilterOption>): string {
  const result = convertFiltersToUriParams(values)
  return queryString.stringify(result)
}

export function parseUriParams(url: string): Array<AppliedFilterOption> {
  const parsedUrl = queryString.parse(url) as Record<
    string,
    string | string[] | null | undefined
  >
  return parseUriPayload(parsedUrl)
}

export const getFilterValueName = (value: string): string => {
  return value?.slice(0, -2)
}

export function parseUriPayload(
  parsedUrl: Record<string, string | string[] | null | undefined>,
): AppliedFilterOption[] {
  const result: Array<AppliedFilterOption> = []

  for (const field in parsedUrl) {
    const value = parsedUrl[field]

    if (Array.isArray(value)) {
      result.push({ field, values: value })
    }
    if (typeof value === 'string') {
      // TODO: refactor -- wtf is this try / catch for checking string
      // ans - i think it catches values that are already parsed and handles them
      try {
        const parsed = JSON.parse(value) as
          | { min?: number; max?: number; value_type?: number }
          | string[]
          | number
          | boolean
        if (
          (typeof parsed === 'number' && field.startsWith('theme')) ||
          typeof parsed === 'boolean'
        )
          JSON.parse('cringe and refactor')
        if (
          typeof parsed === 'object' &&
          !Array.isArray(parsed) &&
          parsed.min !== undefined &&
          parsed.max !== undefined
        ) {
          result.push({
            field,
            min: parsed.min,
            max: parsed.max,
            value_type: parsed?.value_type ?? 0,
          } as AppliedFilterOptionRange)
        } else if (Array.isArray(parsed)) {
          result.push({ field, values: parsed })
        }
      } catch (_e) {
        handleException(_e, {
          operation: 'filters.value.parse',
          disposition: 'expected',
          isExpectedError: (cause) => cause instanceof SyntaxError,
          reason: 'Legacy non-JSON filter values remain literal strings.',
        })

        result.push({ field, values: [value] })
      }
    }
  }

  return result
}

interface Makrs {
  [index: number]: object
}

export function generateSliderMarks(
  start: number,
  end: number,
  step: number,
): Makrs {
  const mark = {
    label: null,
  }
  const marks: Makrs = {}
  let next: number = Math.ceil(start / step) * step
  if (end - start <= step) {
    marks[start - step] = mark
    marks[start] = mark
    marks[end + step] = mark
  } else {
    marks[start] = mark
    while (next < end) {
      marks[parseFloat(next.toFixed(2))] = mark
      next += step
    }
    marks[end] = mark
  }
  return marks
}

export const getBadgeLabels = (
  value: AppliedFilterOption,
  options: FilterOption[] | undefined,
): string[] | undefined => {
  if (value?.field.match('sentiment')) {
    // A numeric range (a clicked ridgeline bucket) has no values; label it
    // by its bounds. Bounds are kept as given: 0 is a real edge.
    const { min, max } = value as Partial<AppliedFilterOptionRange>
    if (typeof min === 'number' && typeof max === 'number') {
      return [`${min.toFixed(1)} to ${max.toFixed(1)}`]
    }
    return (
      (value as AppliedFilterOptionSelect)?.values?.filter(
        (item) => item !== '0',
      ) ?? []
    )
  }
  if (
    value?.field.match('create_time') ||
    value.field === 'create_time' ||
    value?.field.match('process_time') ||
    value.field === 'process_time'
  ) {
    return [
      formatUTCDateToCurrentTimezoneDate(
        moment.unix((value as AppliedFilterOptionRange).min),
      ).format('MM/DD/yy') +
        ' - ' +
        formatUTCDateToCurrentTimezoneDate(
          moment.unix((value as AppliedFilterOptionRange).max),
        ).format('MM/DD/yy'),
    ]
  }

  if (value?.field.match('score')) {
    // Discrete-value rating filters (e.g., "unknown" for null ratings from
    // a chart click) carry their labels in `values`; render those rather
    // than falling through to the Reviews/Ratings range label.
    const valueList = (value as AppliedFilterOptionSelect)?.values
    if (Array.isArray(valueList) && valueList.length > 0) {
      return valueList
    }
    const type =
      (value as AppliedFilterOptionRange).value_type === 1
        ? 'Reviews'
        : (value as AppliedFilterOptionRange).value_type === 2
          ? 'Ratings'
          : 'Reviews & Ratings'
    const min = (value as FilterOptionRange).min
    const max = (value as FilterOptionRange).max
    return min && max ? [type + ' ' + min + ' - ' + max] : [type]
  }

  // with theme and proj_uuids have uuid's that have to be converted to their labels
  if (value?.field.match('theme') || value?.field.match('proj_uuid')) {
    const foundOption = options?.find(
      (option) => option?.field === value?.field,
    )

    if (foundOption) {
      return (foundOption as FilterOptionSelect)?.values
        ?.filter(
          (item) =>
            (value as AppliedFilterOptionSelect).values.includes(item.value) ||
            (item.cluster_uuid &&
              (value as AppliedFilterOptionSelect).values.includes(
                item.cluster_uuid,
              )),
        )
        .map((el) => {
          if (
            foundOption?.field === 'theme' &&
            el.value.split(': ').length > 1 &&
            (el.value.split(': ')[0] === el.value.split(': ')[1] ||
              el.value.includes('Fans / Attractors') ||
              el.value.includes('Critics / Detractors'))
          ) {
            return el.value.split(':')[0] ?? el.value
          }
          return el.value
        })
    }
  }

  // generalize to recognize custom range fields without select boxes
  if (
    value &&
    Object.prototype.hasOwnProperty.call(value, 'field') &&
    Object.prototype.hasOwnProperty.call(value, 'max')
  ) {
    const type = value?.field
    const min = (value as FilterOptionRange).min
    const max = (value as FilterOptionRange).max
    return min && max ? [type + ' ' + min + ' - ' + max] : [type]
  }

  return _.cloneDeep((value as AppliedFilterOptionSelect)?.values) ?? []
}

// DATES

export const formatDateToUTCMidnight = (date: moment.Moment): moment.Moment => {
  return date.utc().set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  })
}

export const formatDateToUTCEndOfDay = (date: moment.Moment): moment.Moment => {
  return date.utc().set({
    hour: 23,
    minute: 59,
    second: 0,
    millisecond: 0,
  })
}

export const formatUTCDateToCurrentTimezoneDate = (
  date: moment.Moment,
): moment.Moment => {
  return moment(date.utc().format('MM/DD/YYYY HH:mm'), 'MM/DD/YYYY HH:mm')
}

export const formatDateToMidnight = (date: moment.Moment): moment.Moment => {
  return moment(
    moment(date.format('MM/DD/YYYY HH:mm'), 'MM/DD/YYYY HH:mm').set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    }),
  )
}

export const formatDateToEndOfDay = (date: moment.Moment): moment.Moment => {
  return moment(
    moment(date.format('MM/DD/YYYY HH:mm'), 'MM/DD/YYYY HH:mm').set({
      hour: 23,
      minute: 59,
      second: 0,
      millisecond: 0,
    }),
  )
}

export const updateFilterList = (
  filterList: FilterOption[],
  openedOptions: string[],
  values: FilterOption[],
): FilterOption[] => {
  const temp: FilterOption[] = JSON.parse(JSON.stringify(filterList))
  if (temp.length > 0 && openedOptions.length) {
    temp.forEach((el, index) => {
      temp[index] = { ...el, ...values[index] }
    })
  } else {
    return values
  }
  return temp
}
